import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { companyService } from 'src/app/services/company.service';
import { Router } from '@angular/router';
import { userDataService } from 'src/app/services/userDataService';
import { RescheduleModalComponent } from '../app-reschedule-modal/app-reschedule-modal.component';
import { leadsRoute } from '@app/leads/admissions.routes';

@Component({
  selector: 'app-mark-done-modal',
  standalone: true,
  imports: [MaterialModule, CommonModule, FormsModule],
  templateUrl: './app-mark-done-modal.component.html',
  styleUrls: ['./app-mark-done-modal.component.scss']
})
export class MarkDoneModalComponent implements OnInit {
  selectedStatus: string = '';
  selectedOutcome: number | null = null;
  activityOutcomes: any[] = [];
  isOutcomeRequired: boolean = false;
  isLoading: boolean = false;
  isLoadingOutcomes: boolean = false;
  showOutcomes: boolean = false;

  roleUser:'ADMIN' | 'USER' = 'USER'

  constructor(
    private dialogRef: MatDialogRef<MarkDoneModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private companyService: companyService,
    private router: Router,
    private dialog: MatDialog,
    private userDataService: userDataService
  ) {
    // Set outcome requirement based on activity type
    this.isOutcomeRequired = this.data?.activity?.type?.is_outcome_required || false;
  }

  ngOnInit() {
    if (this.isAppointmentActivity()) {
      this.preLoadOutcomes();
    }
  
    // Pre-select status if opened from chip click
    if (this.data?.initialStatus) {
      this.selectedStatus = this.data.initialStatus;
      if (this.data.initialStatus === 'SHOW') {
        this.loadAndShowOutcomes();
      } else if (this.data.initialStatus === 'RESCHEDULED') {
        this.openRescheduleModal();
      }
    }
  }

  isAppointmentActivity(): boolean {
    // Simplified version to avoid repeated calls
    return this.data?.activity?.type?.is_appointment || false;
  }

  async preLoadOutcomes() {
    const activityTypeId = this.data?.activity?.type?.id;
    
    if (!activityTypeId) {
      return;
    }

     const user = await this.userDataService.getUserData();
     this.roleUser = user?.role

    try {
      const response: any = await firstValueFrom(
        this.companyService.getActivityTypeOutcomes(activityTypeId)
      );
      
      this.activityOutcomes = response || [];
    } catch (error) {
      this.activityOutcomes = [];
    }
  }

  async loadOutcomes() {
    const activityTypeId = this.data?.activity?.type?.id;
    
    if (!activityTypeId) {
      return;
    }
    
    try {
      const response: any = await firstValueFrom(
        this.companyService.getActivityTypeOutcomes(activityTypeId)
      );
      
      this.activityOutcomes = response || [];
    } catch (error) {
      this.activityOutcomes = [];
    }
  }

  onStatusChange(event: any) {
    this.selectedStatus = event.value;
    this.selectedOutcome = null; // Reset outcome when status changes

    if (event.value === 'SHOW' && this.data?.activity?.type?.is_appointment) {
      this.loadAndShowOutcomes();
    } else if (event.value === 'RESCHEDULED') {
      this.showOutcomes = false;
      this.openRescheduleModal();
    } else {
      this.showOutcomes = false;
    }
  }

  openRescheduleModal(): void {
    const dialogRef = this.dialog.open(RescheduleModalComponent, {
      width: '550px',
      data: { activity: this.data?.activity }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Close mark-done modal immediately with reschedule result
        this.dialogRef.close({
          status: 'RESCHEDULED',
          completed: false,
          outcome_id: null,
          rescheduleData: result
        });
      } else {
        // User cancelled - reset selection
        this.selectedStatus = '';
      }
    });
  }

  async loadAndShowOutcomes() {
    this.isLoadingOutcomes = true;
    this.showOutcomes = true;
    
    // Load outcomes if not already loaded
    if (this.activityOutcomes.length === 0) {
      await this.loadOutcomes();
    }
    
    this.isLoadingOutcomes = false;
  }

  handleConfirmClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    
    this.confirmSelection();
  }

  confirmSelection() {
    // If enroll_lead is enabled, open EnrollmentModal first (similar to reschedule)
    if (this.isEnrollLead()) {
      this.openEnrollmentModal();
      return;
    }

    // If mark_lead_as_lost is enabled, open confirmation modal first
    if (this.isMarkLeadAsLost()) {
      this.openConfirmLostModal();
      return;
    }

    // If cancel status, open cancel note modal
    if (this.selectedStatus === 'CANCEL') {
      this.openCancelNoteModal();
      return;
    }

    this.isLoading = true;

    // Small delay for better UX
    setTimeout(() => {
      this.closeWithResult();
    }, 300);
  }

  /**
   * Open EnrollmentModal when enroll_lead outcome is selected
   * Similar to openRescheduleModal flow
   */
  async openEnrollmentModal(): Promise<void> {
    const { EnrollmentModalComponent } = await import(
      '../../enrollment-modal/enrollment-modal.component'
    );

    const dialogRef = this.dialog.open(EnrollmentModalComponent, {
      width: '400px',
      disableClose: true,
      data: { title: 'Start Date', mode: 'enroll', fa_advisor_id: this.data?.activity?.lead?.fa_advisor_id ?? null }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Close mark-done modal with combined result (similar to reschedule)
        const selectedOutcomeData = this.getSelectedOutcomeData();

        const startAt = result.start_at instanceof Date
          ? result.start_at.toISOString()
          : new Date(result.start_at).toISOString();

        this.dialogRef.close({
          status: this.selectedStatus,
          completed: true,
          outcome_id: this.selectedOutcome,
          ask_for_new_activity: selectedOutcomeData?.outcome?.ask_for_new_activity || false,
          mark_lead_as_lost: selectedOutcomeData?.outcome?.mark_lead_as_lost || false,
          lost_reason_id: selectedOutcomeData?.outcome?.lost_reason_id || null,
          enroll_lead: true,
          lead: this.data?.activity?.lead,
          enrollmentData: {
            start_at: startAt,
            note: result.note,
            fa_advisor_id: result.fa_advisor_id ?? null
          }
        });
      }
      // User cancelled - stay in mark-done modal (don't reset status)
    });
  }

  /**
   * Open ConfirmLostModal when mark_lead_as_lost outcome is selected
   * Similar to openEnrollmentModal flow
   */
  async openConfirmLostModal(): Promise<void> {
    const { ConfirmLostModalComponent } = await import(
      './confirm-lost-modal/confirm-lost-modal.component'
    );

    const selectedOutcomeData = this.getSelectedOutcomeData();
    const lostReasonId = selectedOutcomeData?.outcome?.lost_reason_id || null;

    const dialogRef = this.dialog.open(ConfirmLostModalComponent, {
      width: '400px',
      disableClose: true,
      data: { lost_reason_id: lostReasonId }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close({
          status: this.selectedStatus,
          completed: true,
          outcome_id: this.selectedOutcome,
          ask_for_new_activity: selectedOutcomeData?.outcome?.ask_for_new_activity || false,
          mark_lead_as_lost: result.mark_lead_as_lost,
          lost_reason_id: result.lost_reason_id,
          enroll_lead: false,
          lead: this.data?.activity?.lead
        });
      }
      // User cancelled - stay in mark-done modal (don't reset status)
    });
  }

  async openCancelNoteModal(): Promise<void> {
    const { ConfirmLostModalComponent } = await import(
      './confirm-lost-modal/confirm-lost-modal.component'
    );

    const dialogRef = this.dialog.open(ConfirmLostModalComponent, {
      width: '400px',
      disableClose: true,
      data: { lead_id: this.data?.activity?.lead?.id, mode: 'cancel_note' }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dialogRef.close({
          status: 'CANCEL',
          completed: false,
          outcome_id: null,
          mark_lead_as_lost: result.mark_lead_as_lost,
          lost_reason_id: result.lost_reason_id,
          lead: this.data?.activity?.lead
        });
      } else {
        this.selectedStatus = '';
      }
    });
  }

  selectOutcome(outcomeId: number) {
    this.selectedOutcome = outcomeId;
  }

  /**
   * Get the selected outcome data
   */
  getSelectedOutcomeData(): any {
    return this.activityOutcomes.find(
      o => o.outcome?.id === this.selectedOutcome
    );
  }

  /**
   * Check if selected outcome will mark lead as lost
   */
  isMarkLeadAsLost(): boolean {
    const selectedOutcomeData = this.getSelectedOutcomeData();
    return selectedOutcomeData?.outcome?.mark_lead_as_lost || false;
  }

  /**
   * Check if selected outcome will enroll lead
   */
  isEnrollLead(): boolean {
    const selectedOutcomeData = this.getSelectedOutcomeData();
    return selectedOutcomeData?.outcome?.enroll_lead || false;
  }

  /**
   * Get the confirm button text based on selected outcome
   */
  getConfirmButtonText(): string {
    if (this.isMarkLeadAsLost()) {
      return 'Confirm and Close';
    }
    if (this.isEnrollLead()) {
      return 'Confirm and Enroll';
    }
    return 'Confirm';
  }

  /**
   * Check if an outcome should be disabled based on lead status
   * - mark_lead_as_lost outcomes are disabled for Won leads
   */
  isOutcomeDisabled(outcome: any): boolean {
    const lead = this.data?.activity?.lead;
    const needsLead = outcome?.outcome?.enroll_lead || outcome?.outcome?.mark_lead_as_lost;
    if (needsLead && !lead) {
      return true;
    }
    if (outcome?.outcome?.mark_lead_as_lost && lead?.status === 'Won') {
      return true;
    }
    if (outcome?.outcome?.enroll_lead && lead?.enrolled_at) {
      return true;
    }
    if (outcome?.outcome?.enroll_lead && lead?.isDuplicated) {
      return true;
    }
    return false;
  }


  getOutcomeDisabledTooltip(outcome: any): string {
    const lead = this.data?.activity?.lead;
    const needsLead = outcome?.outcome?.enroll_lead || outcome?.outcome?.mark_lead_as_lost;
    if (needsLead && !lead) {
      return 'Attach a lead to use this outcome';
    }
    if (outcome?.outcome?.mark_lead_as_lost && lead?.status === 'Won') {
      return 'Cannot mark a Won lead as Lost';
    }
    if (outcome?.outcome?.enroll_lead && lead?.enrolled_at) {
      return 'Lead is already enrolled';
    }
    if (outcome?.outcome?.enroll_lead && lead?.isDuplicated) {
      return 'Cannot enroll a duplicated lead';
    }
    return '';
  }

  private closeWithResult() {
    const selectedOutcomeData = this.getSelectedOutcomeData();

    const result = {
      status: this.selectedStatus,
      completed: true,
      outcome_id: this.selectedOutcome,
      ask_for_new_activity: selectedOutcomeData?.outcome?.ask_for_new_activity || false,
      mark_lead_as_lost: selectedOutcomeData?.outcome?.mark_lead_as_lost || false,
      lost_reason_id: selectedOutcomeData?.outcome?.lost_reason_id || null,
      enroll_lead: selectedOutcomeData?.outcome?.enroll_lead || false,
      // Pass the lead from the activity for prefilling new activity, closing, or enrollment
      lead: this.data?.activity?.lead
    };

    this.dialogRef.close(result);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getDialogTitle(): string {
    return 'Mark Appointment as Done';
  }

  goToOutcomeSettings(): void {
    // Close all open dialogs
    this.dialog.closeAll();

    // Navigate to settings page with both tab and subTab parameters
    this.router.navigate(['/settings/profile/company'], {
      queryParams: { tab: 'activities', subTab: 'outcomes' }
    });
  }
  
}